import React, { memo, useState, ChangeEvent } from 'react';
import styled from 'styled-components';
import ModalContainer, { ButtonGroup } from '@/shared/components/modal/ModalContainer';
import StyledInput from '@/shared/components/atoms/StyledInput';
import StyledPassword from '@/shared/components/atoms/StyledPassword';
import StyledButton from '@/shared/components/atoms/StyledButton';
import theme from '@/styles/theme';
import { useReceptionAdminControllerEnterV1 } from '@/api/generated/admin-reception/admin-reception';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { paths } from '@/shared/config/paths';
import { useReceptionMode } from '../hooks/useReceptionMode';

type StaffChangeModalProps = {
	isCreate?: boolean;
	// 체크인 대상 (isCreate=true 일 때 리셉션 모드 진입 API로 서버에 저장됨)
	contestId?: string;
	sessionId?: number;
	title?: string;
	onClose: () => void;
};

// 담당자 변경 모달
const StaffChangeModal = memo<StaffChangeModalProps>(({ isCreate = false, contestId, sessionId, title, onClose }) => {
	const navigate = useNavigate();
	const { changeManager } = useReceptionMode();
	const [managerName, setManagerName] = useState('');
	const [password, setPassword] = useState('');

	const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => setManagerName(e.target.value);
	const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);

	const { mutate: enterReception } = useReceptionAdminControllerEnterV1({
		mutation: {
			onSuccess: () => {
				onClose();
				navigate(paths.onsiteCheckIn.scan.path, { replace: true });
			}
		}
	});

	const isDisabled = !managerName.trim() || !password;

	const handleSubmit = async () => {
		if (!managerName) {
			toast.error('담당자 이름을 입력해주세요.');
			return;
		}

		if (!password) {
			toast.error('비밀번호를 입력해주세요.');
			return;
		}

		if (isCreate) {
			// 담당자 등록(리셉션 모드 최초 진입) API 호출
			enterReception({
				data: {
					managerName,
					password,
					contestId,
					sessionId,
					title
				}
			});
		} else {
			// 담당자 변경 API 호출
			try {
				await changeManager(managerName, password);
				onClose();
			} catch {
				// 실패 메시지는 axios 인터셉터에서 toast로 표시됨
			}
		}
	};

	return (
		<ModalContainer maxWidth={480} title={isCreate ? '담당자 등록하기' : '담당자 변경하기'} onClose={onClose}>
			<Description>{isCreate ? '새로운 담당자를 등록하시겠어요?' : '현재 시간부로 담당자를 변경하시겠어요?'}</Description>
			<StyledInput
				title="담당자 이름"
				lengthCount={true}
				maxLength={40}
				value={managerName}
				onChange={handleNameChange}
				placeholder="담당자 이름을 입력해주세요"
			/>
			<Field>
				<FieldLabel>로그인 비밀번호</FieldLabel>
				<StyledPassword
					value={password}
					onKeyPress={e => e.key === 'Enter' && handleSubmit()}
					onChange={handlePasswordChange}
					placeholder="비밀번호를 입력해주세요."
				/>
			</Field>
			<ButtonGroup>
				<StyledButton width={130} onClick={onClose}>
					취소
				</StyledButton>
				<StyledButton
					width={200}
					fontColor={theme.colors.whiteColor}
					bgColor={theme.colors.activeSkyBlue}
					border="none"
					disabled={isDisabled}
					onClick={handleSubmit}>
					담당자 {isCreate ? '등록' : '변경'}
				</StyledButton>
			</ButtonGroup>
		</ModalContainer>
	);
});

StaffChangeModal.displayName = 'StaffChangeModal';

export default StaffChangeModal;

const Description = styled.div`
	font-size: 14px;
	color: ${({ theme }) => theme.colors.deepGrayFontColor};
	margin-bottom: 24px;
`;
const Field = styled.div`
	margin-top: 20px;
`;
const FieldLabel = styled.div`
	font-size: 14px;
	margin-bottom: 8px;
`;
